import React, { useState, useRef } from 'react';
import { importService } from '../services/importService';
import './ImportButton.css';
import ImportResultModal from './ImportResultModal';
import { Modal } from 'antd';

const ImportButton = ({ 
  type = 'products',
  label = 'Importer Produits',
  icon = 'fas fa-file-import',
  className = '',
  onSuccess,
  onError 
}) => {
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const fileInputRef = useRef(null);

  const handleImport = async (file) => {
    if (!file) return;

    // Vérifier le type de fichier
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv' // .csv
    ];

    if (!allowedTypes.includes(file.type)) {
      const error = 'Veuillez sélectionner un fichier Excel (.xlsx, .xls) ou CSV';
      if (onError) {
        onError(error);
      } else {
        alert(error);
      }
      return;
    }

    setIsImporting(true);
    setImportProgress(0);

    try {
      const result = await importService.importProducts(file, (progress) => {
        setImportProgress(progress);
      });

      if (result.success) {
        setResult(result);
        setModalOpen(true);

        // Affichage immédiat avec Modal statique pour éviter les remontages
        const { imported = 0, errors = 0, details = [] } = result;
        const downloadErrorsCsv = () => {
          if (!details || details.length === 0) return;
          const rows = [['Ligne','Produit','Erreur']];
          details.forEach(d => {
            if (d.error) {
              rows.push([ (d.index ?? '') + 2, d.product || '', d.error || '' ]);
            }
          });
          const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/\"/g,'""')}"`).join(',')).join('\n');
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = `erreurs-import-${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
        };

        Modal.info({
          title: "Résultat de l'import",
          content: (
            <div>
              <div style={{ display:'flex', gap:16, marginBottom:12 }}>
                <div style={{ background:'#f6ffed', border:'1px solid #b7eb8f', borderRadius:8, padding:12, flex:1 }}>
                  <div style={{ color:'#389e0d', fontWeight:600 }}>Importés</div>
                  <div style={{ fontSize:22, fontWeight:700 }}>{imported}</div>
                </div>
                <div style={{ background:'#fff1f0', border:'1px solid #ffa39e', borderRadius:8, padding:12, flex:1 }}>
                  <div style={{ color:'#cf1322', fontWeight:600 }}>Erreurs</div>
                  <div style={{ fontSize:22, fontWeight:700 }}>{errors}</div>
                </div>
              </div>
              {errors > 0 && (
                <button onClick={downloadErrorsCsv} style={{ background:'#1677ff', color:'#fff', border:0, borderRadius:6, padding:'8px 12px', cursor:'pointer' }}>
                  Télécharger le CSV des erreurs
                </button>
              )}
            </div>
          ),
          okText: 'Fermer'
        });
        if (onSuccess) {
          onSuccess(result);
        }
      } else {
        throw new Error(result.error || 'Erreur lors de l\'import');
      }

    } catch (error) {
      console.error('Erreur d\'import:', error);
      if (onError) {
        onError(error.message || 'Erreur lors de l\'import');
      }
    } finally {
      setIsImporting(false);
      setImportProgress(0);
      // Réinitialiser l'input file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleImport(file);
    }
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getButtonLabel = () => {
    if (isImporting) {
      return `Import... ${importProgress}%`;
    }
    return label;
  };

  return (
    <>
      <ImportResultModal open={modalOpen} onClose={() => setModalOpen(false)} result={result} />
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <button
        className={`import-button ${className}`}
        onClick={handleButtonClick}
        disabled={isImporting}
        title={getButtonLabel()}
      >
        {isImporting ? (
          <i className="fas fa-spinner fa-spin"></i>
        ) : (
          <i className={icon}></i>
        )}
        <span>{getButtonLabel()}</span>
        {isImporting && (
          <div className="import-progress">
            <div 
              className="import-progress-bar" 
              style={{ width: `${importProgress}%` }}
            ></div>
          </div>
        )}
      </button>
    </>
  );
};

export default ImportButton;
